/**
 * Extension state snapshot
 *
 * Module-level mirror of the current `useExtensionState` value so that pure
 * helpers like `enrichRedirectUrl` / `convertToRedirectUrl` can attach
 * `extension={installed,version}` onto redirect URLs without every call site
 * having to thread the state through props.
 *
 * The snapshot is populated by `useExtensionState` via a `useEffect` on mount
 * and whenever the PING/AUTH_SYNC postMessage updates the hook's state. Click
 * handlers always run after that first populate, so by the time a user clicks
 * a buy link the snapshot reflects the live install state.
 *
 * Mount point: `useExtensionState` is called by `ExtensionModalProvider` in
 * `apps/web/src/pages/_app.tsx`, which wraps every page. That is currently the
 * only thing keeping this snapshot populated app-wide. If that provider is
 * ever conditionally skipped, extension attribution will silently break on the
 * affected routes — mount `useExtensionState` somewhere along the new route's
 * tree or restore the provider.
 *
 * BROWSER-ONLY: `setExtensionStateSnapshot` must never be called server-side.
 * In Next.js, Node.js module singletons are shared across all concurrent SSR
 * requests, so writing to this snapshot on the server would contaminate every
 * in-flight response. All current callers are client-only (the hook + tests).
 *
 * Timing note: components that memoize redirect URLs (e.g. ProductListCard's
 * useMemo for href) read the snapshot at render time, not click time. If the
 * extension PING arrives after the memo is computed but before a re-render,
 * the cached URL won't include `extension=`. In practice this is a near-zero
 * window because data loading triggers re-renders that recompute the memo
 * before the user can interact.
 *
 * Tests can drive the snapshot directly via `setExtensionStateSnapshot` and
 * should call `resetExtensionStateSnapshot` in `afterEach` to stay hermetic.
 */

export type ExtensionStateSnapshot = {
  installed?: boolean
  version?: string
}

let snapshot: ExtensionStateSnapshot = {}

export const setExtensionStateSnapshot = (
  next: ExtensionStateSnapshot
): void => {
  snapshot = {
    installed: next.installed,
    version: next.version,
  }
}

export const getExtensionStateSnapshot = (): ExtensionStateSnapshot => snapshot

/** Test helper — reset the module-level state between tests. */
export const resetExtensionStateSnapshot = (): void => {
  snapshot = {}
}
